// src/components/crm/ResolveFlagModal.js
// PHASE 1.3 VERIFIER: r2-8b1d4f0c
//
// Modal for resolving a flagged note. Captures an optional resolution note
// (max 500 chars to match the CF cap) and calls resolveNoteFlag.
//
// Mobile-friendly:
//   - Bottom sheet on small screens (slides up from bottom — natural mobile UX)
//   - Centered card on >=sm screens
//   - Tap-friendly button sizes (~36px tall)
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. The CF is the authoritative gate — this modal trusts that NoteCard's
//      permission check was correct, but the CF re-validates. A user who
//      somehow opened this modal without permission would be rejected at
//      the CF layer with HttpsError 'permission-denied'.
//   2. resolutionNote is hard-capped at 500 chars at the textarea AND again
//      at payload build (belt and suspenders) to match the CF's cap exactly.
//   3. isSubmitting guard prevents double-submit on rapid clicks; the CF
//      transactional state machine is the second line of defense.
//   4. On success the modal closes and the realtime onSnapshot listener in
//      useOrgNotes will reflect the resolved state automatically — no
//      explicit refetch or local-state mutation needed.
//   5. CF errors surface their HttpsError .message to the user; falls back
//      to a generic message if .message is missing.

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CheckCircle2, X } from 'lucide-react';

const MAX_RESOLUTION_NOTE_LENGTH = 500;

// Resolves once at module load against the default Firebase app — picks up
// any emulator connection set in firebase/config. If your config exports
// `functions` directly, you can swap in:
//   import { functions } from '../../firebase/config';
const resolveNoteFlagFn = httpsCallable(getFunctions(), 'resolveNoteFlag');

const ResolveFlagModal = ({ note, orgId, onClose }) => {
  const [resolutionNote, setResolutionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleTextChange = (e) => {
    setResolutionNote(e.target.value.slice(0, MAX_RESOLUTION_NOTE_LENGTH));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!orgId || !note?.id) {
      setError('Missing required information.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const trimmed = resolutionNote.trim().slice(0, MAX_RESOLUTION_NOTE_LENGTH);
      await resolveNoteFlagFn({
        orgId,
        noteId: note.id,
        resolutionNote: trimmed.length > 0 ? trimmed : null,
      });
      onClose();
    } catch (err) {
      console.error('ResolveFlagModal: resolve failed', err);
      setError(err?.message || 'Could not resolve flag. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-800">Resolve flag</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-md disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-600">
            Mark this flag as resolved? You can optionally add a short note
            describing what was done.
          </p>

          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">
              Resolution note (optional)
            </label>
            <textarea
              value={resolutionNote}
              onChange={handleTextChange}
              placeholder="e.g. Called customer back, scheduled for Tuesday."
              rows={3}
              maxLength={MAX_RESOLUTION_NOTE_LENGTH}
              disabled={isSubmitting}
              className="w-full text-sm border border-slate-200 rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-50"
            />
            <p className="text-xs text-slate-400 text-right mt-1">
              {resolutionNote.length} / {MAX_RESOLUTION_NOTE_LENGTH}
            </p>
          </div>

          {error && (
            <p className="text-xs text-red-600">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 pt-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={14} />
            {isSubmitting ? 'Resolving…' : 'Resolve flag'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResolveFlagModal;